import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getBookingPageBySlug,
  createPublicAppointment,
} from '../api/publicBookingApi';
import {
  requiresApproval,
  getTotalServiceDuration,
  isAppointmentSetupComplete,
  shouldDisableContinueButton,
  buildAppointmentPayload,
} from '../utility/bookingFlowUtils';

/**
 * Custom hook that owns all public booking wizard state, React Query calls,
 * and handler functions. UI/DOM concerns (refs, scroll effects, theme) stay
 * in the page component.
 *
 * @param {string} slug - Booking page slug from useParams
 */
const usePublicBookingFlow = (slug) => {
  // Wizard navigation
  const [currentBookingStep, setCurrentBookingStep] = useState('services');

  // Service selection
  const [selectedServices, setSelectedServices] = useState([]);

  // Appointment configuration
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [travelFee, setTravelFee] = useState(null);

  // Approval flags
  const [outsideAvailabilityRequested, setOutsideAvailabilityRequested] = useState(false);
  const [customTimeRequested, setCustomTimeRequested] = useState(false);

  // Contact form initial data (for form initialization only)
  const [contactFormData, setContactFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    additionalNotes: '',
  });

  // Submission / error state
  const [bookingResult, setBookingResult] = useState(null);
  const [currentBookingError, setCurrentBookingError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [contactFormValidationFailed, setContactFormValidationFailed] = useState(false);

  // -------------------------------------------------------------------------
  // React Query — fetch booking page data
  // -------------------------------------------------------------------------

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['public-booking', slug],
    queryFn: () => getBookingPageBySlug(slug),
    enabled: !!slug,
    retry: (failureCount, err) => {
      if (err?.message === 'Booking page not found') {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Destructure booking page data (safe when data is available)
  const bookingPage = data?.bookingPage ?? null;
  const services = data?.services ?? null;
  const serviceLocationTypes = data?.serviceLocationTypes ?? null;
  const fixedLocationData = data?.fixedLocationData ?? null;
  const currency = data?.currency ?? 'CAD';
  const searchBias = data?.searchBias ?? null;

  // -------------------------------------------------------------------------
  // React Query — create appointment mutation
  // -------------------------------------------------------------------------

  // Ref to the ContactConfirmationStep imperative handle. Set in
  // handleSubmitBooking from the contactFormRef passed down by the context.
  // Used inside the mutation's onError to attach backend validation errors
  // inline on the right field via setError.
  const contactFormRefHolder = useRef(null);

  const createAppointmentMutation = useMutation({
    mutationFn: (appointmentData) => createPublicAppointment(slug, appointmentData),
    onSuccess: (result) => {
      setBookingResult(result);
      setCurrentBookingError(null);
      setValidationErrors({});
    },
    onError: (err) => {
      // Slot conflict — the appointment-setup step owns this error; reset
      // the date/time selection and bounce the user back.
      if (err.code === "SLOT_CONFLICT") {
        setSelectedDate(null);
        setSelectedTime(null);
        setCustomTimeRequested(false);
        setOutsideAvailabilityRequested(false);
        setTravelFee(null);
        setCurrentBookingStep('appointment-setup');
        setCurrentBookingError(err.message);
        setValidationErrors({});
        return;
      }

      // Backend validation errors — attach each one inline on its field.
      // Fields not on the contact form (services, appointmentType, dateTime)
      // still go into the banner as a fallback.
      if (err.validationErrors && err.validationErrors.length > 0) {
        const contactFormFields = new Set([
          'firstName',
          'lastName',
          'email',
          'phoneNumber',
          'additionalNotes',
        ]);
        const inlineErrors = {};
        const bannerErrors = {};
        err.validationErrors.forEach(({ field, message }) => {
          if (contactFormFields.has(field)) {
            inlineErrors[field] = message;
          } else {
            bannerErrors[field] = message;
          }
        });

        if (contactFormRefHolder.current?.setFieldErrors) {
          contactFormRefHolder.current.setFieldErrors(inlineErrors);
        }

        setValidationErrors(bannerErrors);
        // Only show a banner message when there are fields we couldn't
        // surface inline. Otherwise the inline errors are enough.
        if (Object.keys(bannerErrors).length > 0) {
          setCurrentBookingError('Please fix the issues below to continue.');
        } else {
          setCurrentBookingError(null);
        }
        return;
      }

      // Network error (publicBookingApi throws plain Error for 5xx / unknown)
      // — surface a sanitized banner. Never show raw backend messages on 5xx.
      const fallback =
        err.message === 'Server error occurred while creating appointment'
          ? 'Something went wrong. Please try again in a moment.'
          : err.message || 'Failed to create appointment';
      setCurrentBookingError(fallback);
      setValidationErrors({});
    },
  });

  // -------------------------------------------------------------------------
  // Computed values
  // -------------------------------------------------------------------------

  const totalServiceDuration = useMemo(
    () => getTotalServiceDuration(selectedServices),
    [selectedServices]
  );

  const appointmentSetupComplete = useMemo(
    () =>
      isAppointmentSetupComplete(
        selectedAppointmentType,
        selectedLocation,
        selectedTime,
        selectedAddress,
        fixedLocationData?.locations?.length ?? 0
      ),
    [selectedAppointmentType, selectedLocation, selectedTime, selectedAddress, fixedLocationData]
  );

  const approvalRequired = useMemo(
    () => requiresApproval(outsideAvailabilityRequested, customTimeRequested, bookingPage?.autoApproval),
    [outsideAvailabilityRequested, customTimeRequested, bookingPage]
  );

  const continueDisabled = useMemo(
    () =>
      shouldDisableContinueButton(
        currentBookingStep,
        appointmentSetupComplete,
        createAppointmentMutation.isPending,
        selectedServices.length > 0
      ),
    [currentBookingStep, appointmentSetupComplete, createAppointmentMutation.isPending, selectedServices]
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const updateServiceQuantity = useCallback((service, quantity) => {
    setSelectedServices((prev) => {
      if (quantity === 0) {
        return prev.filter((item) => item.service._id !== service._id);
      }
      const existingIndex = prev.findIndex((item) => item.service._id === service._id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { service, quantity };
        return updated;
      }
      return [...prev, { service, quantity }];
    });
  }, []);

  const getServiceQuantity = useCallback(
    (serviceId) => {
      const found = selectedServices.find((item) => item.service._id === serviceId);
      return found ? found.quantity : 0;
    },
    [selectedServices]
  );

  /**
   * @param {React.RefObject} contactFormRef - ref to the ContactConfirmationStep component
   */
  const handleSubmitBooking = useCallback(
    (contactFormRef) => {
      // Defense-in-depth double-submit guard (button is also disabled).
      if (createAppointmentMutation.isPending) return;

      setCurrentBookingError(null);
      setValidationErrors({});

      // Remember the ref so the mutation's onError handler can attach
      // backend validation errors inline on the right field.
      contactFormRefHolder.current = contactFormRef?.current ?? null;

      let currentContactFormData;
      try {
        currentContactFormData = contactFormRef?.current?.getFormData();
        if (!currentContactFormData) {
          // Silent fallback — already using contactFormData state
          currentContactFormData = contactFormData;
        } else {
          const { firstName, email } = currentContactFormData;
          if (!firstName || !email) {
            // Silent merge fallback
            currentContactFormData = { ...contactFormData, ...currentContactFormData };
          }
        }
      } catch (err) {
        // TD-006: was console.error — now surfaces error to user
        setCurrentBookingError('Unable to read contact form. Please try again.');
        return;
      }

      const appointmentData = buildAppointmentPayload(
        currentContactFormData,
        selectedServices,
        selectedAppointmentType,
        selectedAddress,
        selectedLocation,
        selectedDate,
        selectedTime,
        travelFee,
        outsideAvailabilityRequested,
        customTimeRequested
      );

      createAppointmentMutation.mutate(appointmentData);
    },
    [
      contactFormData,
      selectedServices,
      selectedAppointmentType,
      selectedAddress,
      selectedLocation,
      selectedDate,
      selectedTime,
      travelFee,
      outsideAvailabilityRequested,
      customTimeRequested,
      createAppointmentMutation,
    ]
  );

  /**
   * @param {React.RefObject} contactFormRef - ref to the ContactConfirmationStep component
   */
  const handleContinueBooking = useCallback(
    async (contactFormRef) => {
      if (currentBookingStep === 'services') {
        setCurrentBookingStep('appointment-setup');
      } else if (currentBookingStep === 'appointment-setup') {
        setCurrentBookingStep('contact-confirmation');
      } else if (currentBookingStep === 'contact-confirmation') {
        setContactFormValidationFailed(false);

        if (contactFormRef?.current) {
          const isValid = await contactFormRef.current.validateForm();
          if (isValid) {
            handleSubmitBooking(contactFormRef);
          } else {
            setContactFormValidationFailed(true);
          }
        } else {
          setCurrentBookingError('Contact form is unavailable. Please refresh and try again.');
        }
      }
    },
    [currentBookingStep, handleSubmitBooking]
  );

  const handleAppointmentTypeChange = useCallback((type) => {
    setSelectedAppointmentType(type);
    if (type === 'fixed') {
      setSelectedAddress(null);
    }
    if (type === 'mobile') {
      setSelectedLocation(null);
    }
    setSelectedDate(null);
    setSelectedTime(null);
    setTravelFee(null);
    setCustomTimeRequested(false);
    setOutsideAvailabilityRequested(false);
  }, []);

  const handleAddressChange = useCallback((address) => {
    setSelectedAddress(address);
  }, []);

  const handleOutsideAvailabilityChange = useCallback((status) => {
    setOutsideAvailabilityRequested(status);
  }, []);

  const handleCustomTimeRequestChange = useCallback((status) => {
    setCustomTimeRequested(status);
  }, []);

  /**
   * Goes back one step and resets associated state.
   * Scroll-to-services behavior is NOT handled here — the page component
   * wraps this handler and adds the scroll effect.
   */
  const handleBackToPreviousStep = useCallback(() => {
    setCurrentBookingError(null);
    setValidationErrors({});
    setContactFormValidationFailed(false);

    if (currentBookingStep === 'contact-confirmation') {
      setCurrentBookingStep('appointment-setup');
      setCustomTimeRequested(false);
    } else if (currentBookingStep === 'appointment-setup') {
      setCurrentBookingStep('services');
      setSelectedAppointmentType(null);
      setSelectedAddress(null);
      setSelectedLocation(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setTravelFee(null);
      setOutsideAvailabilityRequested(false);
      setCustomTimeRequested(false);
    }
  }, [currentBookingStep]);

  const handleResetBookingFlow = useCallback(() => {
    setCurrentBookingStep('services');
    setSelectedServices([]);
    setSelectedAppointmentType(null);
    setSelectedAddress(null);
    setSelectedLocation(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setTravelFee(null);
    setOutsideAvailabilityRequested(false);
    setCustomTimeRequested(false);
    setContactFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      additionalNotes: '',
    });
    setBookingResult(null);
    setCurrentBookingError(null);
    setValidationErrors({});
  }, []);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // Query state
    data,
    isLoading,
    error,
    isError,
    // Destructured booking page data
    bookingPage,
    services,
    serviceLocationTypes,
    fixedLocationData,
    currency,
    searchBias,
    // Wizard state
    currentBookingStep,
    selectedServices,
    selectedAppointmentType,
    selectedAddress,
    selectedLocation,
    selectedDate,
    selectedTime,
    travelFee,
    outsideAvailabilityRequested,
    customTimeRequested,
    contactFormData,
    bookingResult,
    currentBookingError,
    validationErrors,
    contactFormValidationFailed,
    // Computed values
    totalServiceDuration,
    appointmentSetupComplete,
    approvalRequired,
    continueDisabled,
    isSubmittingBooking: createAppointmentMutation.isPending,
    // Handlers
    updateServiceQuantity,
    getServiceQuantity,
    handleContinueBooking,
    handleSubmitBooking,
    handleAppointmentTypeChange,
    handleAddressChange,
    handleOutsideAvailabilityChange,
    handleCustomTimeRequestChange,
    handleBackToPreviousStep,
    handleResetBookingFlow,
    // Direct setters for child components
    setSelectedDate,
    setSelectedTime,
    setSelectedLocation,
    setTravelFee,
  };
};

export default usePublicBookingFlow;
